import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchBeachDayData } from '../services/weatherService';
import { calculateBeachScore, getScoreColor, getScoreEmoji, explainScore } from '../services/scoringService';
import { Sun, Cloud, CloudRain, Wind, Droplets, Waves, Loader, AlertCircle, Thermometer, Eye, Crown, ThumbsUp, ThumbsDown, MessageCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import { formatTemp, formatSpeed, formatDistance } from '../utils/unitConversions';
import { submitDayFeedback, getUserFeedback, suggestPreferenceAdjustments } from '../services/feedbackService';
import { doc as firestoreDoc, updateDoc } from 'firebase/firestore';
import ReferralCard from './ReferralCard';

export default function Dashboard({ onNavigateToPreferences }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [activeLocationIndex, setActiveLocationIndex] = useState(0); // 0 = home, 1+ = additional
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [feedbackSuggestions, setFeedbackSuggestions] = useState([]);
  const [showFeedbackFor, setShowFeedbackFor] = useState(null);
  const confettiTriggered = useRef(false);

  useEffect(() => {
    if (user) {
      loadDashboard();
    }
  }, [user]);

  const loadDashboard = async (locationIndex = 0) => {
    setLoading(true);
    setError(null);

    try {
      // Load user preferences
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      const userPrefs = docSnap.exists() ? docSnap.data().preferences : null;
      setPreferences(userPrefs);

      // Get all locations (home + additional)
      const allLocations = [
        userPrefs?.homeLocation || userPrefs?.homeZipCode
      ];

      // Add additional locations if premium
      if (userPrefs?.isPremium && userPrefs?.additionalLocations) {
        allLocations.push(...userPrefs.additionalLocations.filter(Boolean));
      }

      // Get the active location
      const location = allLocations[locationIndex];

      if (!location) {
        setIsFirstTimeUser(true);
        setLoading(false);
        return;
      }

      // Fetch weather and ocean data (works with both location object and ZIP)
      const data = await fetchBeachDayData(location);

      // Calculate scores for each day
      const scoredForecast = data.forecast.map(day => ({
        ...day,
        scoreData: calculateBeachScore(day, userPrefs, data.hasOceanData)
      }));

      // Filter out past days - only show today and future
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filteredForecast = scoredForecast.filter(day => {
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);
        return dayDate >= today;
      });

      setForecastData({
        ...data,
        forecast: filteredForecast
      });

      // Trigger confetti if there's a perfect day (9.5+) and we haven't triggered yet
      const hasPerfectDay = scoredForecast.some(day => day.score >= 9.5);
      if (hasPerfectDay && !confettiTriggered.current) {
        confettiTriggered.current = true;
        setTimeout(() => {
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.4 },
            colors: ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#87CEEB'],
            shapes: ['circle', 'square'],
            gravity: 0.8,
            drift: 0.1,
            ticks: 200
          });
        }, 500);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIcon = (weatherCode) => {
    if (weatherCode === 0) return Sun;
    if (weatherCode <= 2) return Sun;
    if (weatherCode <= 3) return Cloud;
    return CloudRain;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getPerfectDays = () => {
    if (!forecastData) return [];
    return forecastData.forecast.filter(day => day.scoreData.score >= 9.5);
  };

  // Check if a day is in the past
  const isPastDay = (dateString) => {
    const dayDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dayDate.setHours(0, 0, 0, 0);
    return dayDate < today;
  };

  // Handle user feedback submission
  const handleFeedback = async (day, rating, issues = []) => {
    try {
      await submitDayFeedback(
        user.uid,
        day.date,
        {
          temp: day.tempMax,
          wind: day.windSpeed,
          precipProb: day.precipProb,
          clouds: day.cloudCover,
          waveHeight: day.waveHeight,
          waterTemp: day.waterTemp
        },
        rating,
        issues
      );

      // Load feedback history and check for suggestions
      const feedbackHistory = await getUserFeedback(user.uid, 30);
      const suggestions = suggestPreferenceAdjustments(feedbackHistory, preferences);

      if (suggestions.length > 0) {
        setFeedbackSuggestions(suggestions);
      }

      setShowFeedbackFor(null);
      alert('Thanks for your feedback! 🙏');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  // Apply a suggested preference adjustment
  const applySuggestion = async (suggestion) => {
    try {
      const updatedPrefs = {
        ...preferences,
        [suggestion.type]: suggestion.suggested
      };

      const userRef = firestoreDoc(db, 'users', user.uid);
      await updateDoc(userRef, { preferences: updatedPrefs });
      setPreferences(updatedPrefs);

      // Remove applied suggestion
      setFeedbackSuggestions(prev => prev.filter(s => s.type !== suggestion.type));

      alert(`Updated ${suggestion.type} to ${suggestion.suggested}! Your future scores will reflect this change.`);
    } catch (error) {
      console.error('Error applying suggestion:', error);
      alert('Failed to update preferences. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="wave-loader mb-8">
          <div className="wave"></div>
          <div className="wave"></div>
          <div className="wave"></div>
          <Waves className="absolute inset-0 m-auto w-10 h-10 text-white" />
        </div>
        <p className="text-white text-xl font-semibold animate-pulse">Loading your beach forecast...</p>
      </div>
    );
  }

  // First-time user welcome
  if (isFirstTimeUser) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl text-center">
          <div className="mb-6">
            <Sun className="w-20 h-20 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-3xl font-bold text-gray-800 mb-3">Welcome to 10/10! 🏖️</h3>
            <p className="text-lg text-gray-600 mb-2">
              Let's get you set up for perfect beach day alerts
            </p>
          </div>

          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 mb-6 text-left">
            <h4 className="font-bold text-gray-800 mb-3 text-center">Quick Setup (2 minutes)</h4>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start gap-3">
                <span className="text-2xl">📍</span>
                <div>
                  <strong>Set your beach location</strong>
                  <p className="text-sm text-gray-600">Any city or ZIP code worldwide</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🌡️</span>
                <div>
                  <strong>Customize your preferences</strong>
                  <p className="text-sm text-gray-600">Temperature, wind, clouds (or use our defaults)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🔔</span>
                <div>
                  <strong>Choose alert settings</strong>
                  <p className="text-sm text-gray-600">Email notifications when conditions are perfect</p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={onNavigateToPreferences}
            className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl"
          >
            Set Up My Preferences →
          </button>

          <p className="text-xs text-gray-500 mt-4">
            Takes less than 2 minutes • Free forever • Premium optional
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops!</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadDashboard}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const perfectDays = getPerfectDays();

  // Get all available locations for switcher
  const allLocations = [
    { location: preferences?.homeLocation || preferences?.homeZipCode, label: 'Home', index: 0 }
  ];

  if (preferences?.isPremium && preferences?.additionalLocations) {
    preferences.additionalLocations.forEach((loc, idx) => {
      if (loc) {
        allLocations.push({
          location: loc,
          label: loc.shortName || `Location ${idx + 2}`,
          index: idx + 1
        });
      }
    });
  }

  const showLocationSwitcher = allLocations.length > 1;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Feedback Suggestions Banner */}
      {feedbackSuggestions.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-blue-900 mb-2">💡 We learned from your feedback!</div>
              {feedbackSuggestions.map((suggestion, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 mb-2">
                  <div className="text-sm text-gray-700 mb-2">
                    {suggestion.reason}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">
                      Adjust from <strong>{suggestion.current}</strong> to <strong>{suggestion.suggested}</strong>?
                    </span>
                    <button
                      onClick={() => applySuggestion(suggestion)}
                      className="ml-auto px-3 py-1 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      onClick={() => setFeedbackSuggestions(prev => prev.filter((_, i) => i !== idx))}
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Location Switcher - Premium with multiple locations */}
      {showLocationSwitcher && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl p-4 shadow-lg">
          <div className="flex items-center gap-2 overflow-x-auto">
            <Crown className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex gap-2">
              {allLocations.map((loc) => (
                <button
                  key={loc.index}
                  onClick={() => {
                    setActiveLocationIndex(loc.index);
                    loadDashboard(loc.index);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                    activeLocationIndex === loc.index
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-amber-100'
                  }`}
                >
                  {loc.index === 0 ? '🏠 ' : '📍 '}{loc.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Referral Card */}
      <ReferralCard />

      {/* Location Header */}
      <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {forecastData.locationName || `${forecastData.location.city}, ${forecastData.location.state}`}
            </h2>
            <p className="text-gray-600">7-Day Beach Forecast</p>
          </div>
          {forecastData.current && (
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">
                {formatTemp(forecastData.current.temp, preferences?.units || 'imperial')}
              </div>
              <div className="text-gray-600">Right now</div>
            </div>
          )}
        </div>
        {forecastData.hasOceanData ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-blue-600">
            <Waves className="w-4 h-4" />
            <span>Ocean conditions included in scoring</span>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Ocean data unavailable (Stormglass API limit reached). Scoring based on weather conditions only.</span>
          </div>
        )}
      </div>

      {/* Perfect Days Alert */}
      {perfectDays.length > 0 && (
        <div className="bg-gradient-to-r from-green-400 to-green-500 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="text-4xl">🏖️</div>
            <div>
              <h3 className="text-xl font-bold text-white">
                {perfectDays.length} Perfect Beach Day{perfectDays.length > 1 ? 's' : ''} Coming Up!
              </h3>
              <p className="text-green-50">
                {perfectDays.map(day => formatDate(day.date)).join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Forecast Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {forecastData.forecast.map((day, index) => {
          const WeatherIcon = getWeatherIcon(day.weatherCode);
          const isExpanded = expandedDay === index;
          const score = day.scoreData.score;
          const scoreColor = getScoreColor(score);
          const scoreEmoji = getScoreEmoji(score);

          return (
            <div
              key={day.date}
              className={`score-card bg-white rounded-xl shadow-lg overflow-hidden transition-all cursor-pointer ${
                score >= 9.5 ? 'ring-2 ring-green-400 animate-pulse' : ''
              }`}
              onClick={() => setExpandedDay(isExpanded ? null : index)}
            >
              <div className="p-5">
                {/* Date */}
                <div className="text-sm font-semibold text-gray-500 mb-2">
                  {formatDate(day.date)}
                </div>

                {/* Current Conditions (Today only) */}
                {index === 0 && forecastData.current && (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Now</span>
                      <span className="text-sm font-semibold text-gray-800">{forecastData.current.temp}°</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Rain:</span>
                        <span className="font-semibold text-gray-800">{forecastData.current.precipitation > 0 ? '☔ Yes' : '✓ No'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Wind:</span>
                        <span className="font-semibold text-gray-800">{Math.round(forecastData.current.windSpeed)} mph</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Clouds:</span>
                        <span className="font-semibold text-gray-800">{Math.round(forecastData.current.cloudCover)}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Feels:</span>
                        <span className="font-semibold text-gray-800">{forecastData.current.weatherDescription}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Score with fill bar */}
                <div className="mb-3">
                  <div className={`text-4xl font-bold ${scoreColor} inline-flex items-center gap-2 px-4 py-2 rounded-xl`}>
                    {scoreEmoji} {score}/10
                  </div>
                  {/* Score fill bar */}
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full animate-scoreBar ${scoreColor.replace('text-', 'bg-')}`}
                      style={{ width: `${score * 10}%` }}
                    />
                  </div>
                  {/* Score Explanation */}
                  <div className="mt-2 text-sm text-gray-600 italic">
                    {explainScore(day.scoreData, day, preferences)}
                  </div>
                </div>

                {/* Weather Summary */}
                <div className="flex items-center justify-between mb-3">
                  <WeatherIcon className="w-10 h-10 text-gray-600" />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800">
                      {formatTemp(day.tempMax, preferences?.units || 'imperial').replace(/°[FC]$/, '')}°
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatTemp(day.tempMin, preferences?.units || 'imperial').replace(/°[FC]$/, '')}°
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Wind className="w-4 h-4" /> Wind
                    </span>
                    <span className="font-semibold">{formatSpeed(day.windSpeed, preferences?.units || 'imperial')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Droplets className="w-4 h-4" /> Rain
                    </span>
                    <span className="font-semibold">{day.precipProb.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Cloud className="w-4 h-4" /> Clouds
                    </span>
                    <span className="font-semibold">{Math.round(day.cloudCover)}%</span>
                  </div>
                  {day.waveHeight && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Waves className="w-4 h-4" /> Waves
                      </span>
                      <span className="font-semibold">{formatDistance(day.waveHeight * 3.28, preferences?.units || 'imperial')}</span>
                    </div>
                  )}
                  {day.waterTemp && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Thermometer className="w-4 h-4" /> Water
                      </span>
                      <span className="font-semibold">{formatTemp(day.waterTemp, preferences?.units || 'imperial')}</span>
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                    <div className="text-xs font-semibold text-gray-700 uppercase mb-2">
                      Score Breakdown
                    </div>
                    {day.scoreData.breakdown ? (
                      <>
                        {Object.entries(day.scoreData.breakdown)
                          .filter(([key]) => !['waves', 'waterTemp'].includes(key))
                          .map(([key, data]) => (
                          <div key={key} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 capitalize">
                              {key === 'weatherCode' ? 'Weather' : key === 'ocean' ? 'Ocean' : key === 'precip' ? 'Rain' : key}
                            </span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                                  style={{ width: `${(data / 10) * 100}%` }}
                                />
                              </div>
                              <span className="font-semibold text-gray-700 w-8 text-right">
                                {data.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        ))}
                        {day.scoreData.breakdown.waves !== undefined && (
                          <div className="ml-4 text-xs text-gray-500 flex items-center justify-between">
                            <span>↳ Waves</span>
                            <span className="font-semibold">{day.scoreData.breakdown.waves.toFixed(1)}</span>
                          </div>
                        )}
                        {day.scoreData.breakdown.waterTemp !== undefined && (
                          <div className="ml-4 text-xs text-gray-500 flex items-center justify-between">
                            <span>↳ Water Temp</span>
                            <span className="font-semibold">{day.scoreData.breakdown.waterTemp.toFixed(1)}</span>
                          </div>
                        )}
                        {!day.scoreData.hasOceanData && (
                          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded mt-2">
                            ⚠️ Ocean data unavailable (waves, water temp). Scoring based on weather only.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-500">No breakdown available</div>
                    )}

                    {/* Feedback Section - Only for past days */}
                    {isPastDay(day.date) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-xs font-semibold text-gray-700 uppercase mb-2 flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" /> How was this day?
                        </div>
                        {showFeedbackFor !== index ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowFeedbackFor(index);
                            }}
                            className="w-full py-2 px-3 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                          >
                            Give Feedback
                          </button>
                        ) : (
                          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleFeedback(day, 'perfect')}
                                className="flex-1 py-2 px-3 bg-green-100 text-green-800 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors flex items-center justify-center gap-1"
                              >
                                <ThumbsUp className="w-4 h-4" /> Perfect!
                              </button>
                              <button
                                onClick={() => handleFeedback(day, 'meh')}
                                className="flex-1 py-2 px-3 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                              >
                                Meh
                              </button>
                              <button
                                onClick={() => handleFeedback(day, 'bad')}
                                className="flex-1 py-2 px-3 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-1"
                              >
                                <ThumbsDown className="w-4 h-4" /> Bad
                              </button>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                              <div className="font-medium">What was the issue?</div>
                              <div className="flex flex-wrap gap-1">
                                <button
                                  onClick={() => handleFeedback(day, 'meh', ['too-hot'])}
                                  className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs hover:bg-orange-200"
                                >
                                  Too Hot
                                </button>
                                <button
                                  onClick={() => handleFeedback(day, 'meh', ['too-cold'])}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
                                >
                                  Too Cold
                                </button>
                                <button
                                  onClick={() => handleFeedback(day, 'meh', ['too-windy'])}
                                  className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs hover:bg-gray-200"
                                >
                                  Too Windy
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={() => setShowFeedbackFor(null)}
                              className="w-full text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 text-xs text-center text-gray-400">
                  {isExpanded ? 'Click to collapse' : 'Click for details'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
